import Api from "@/configurations/Api";
import RequestService from "@/configurations/AxiosConfig";
import { ICreatePositionRequest, IExportSingleUserToExcelRequest, IExportSingleUserToPdfRequest, IExportUsersToExcelRequest, IExportUsersToPdfRequest, IResendPasswordEmailRequest, IUsersByCompanyIdAndRoleIdRequest } from "@/types/type";
import { toast } from "sonner";

const endpoints = {
  users: 'users',
  userGroupsByCompanyId: 'users/getUserGroupsByCompanyId',
  changePassword: 'users/change-password',
  profilePhoto: 'users/profilePhoto',
  delete: 'users/delete',
  resendPasswordEmail: 'users/resendPasswordEmail',
  importUsersFromExcel: 'users/importUsersFromExcel',
  getRolesHistory: 'users/roles-history',
};

const UsersService = {
  getAllUsers: () => RequestService.get(endpoints.users),
  createUser: (data: any) => RequestService.post(endpoints.users, data),
  updateUser: (id: number, data: FormData) => RequestService.patch(`${endpoints.users}/${id}`, data), 
  changePassword: (data: any) => RequestService.post(endpoints.changePassword, data),
  getUserById: (id: number | string) => RequestService.get(`${endpoints.users}/${id}`),
  getUsersByRoleId: (roleId: number | string) => RequestService.get(`${endpoints.users}/roleId/${roleId}`),
  getUsersByCompanyId: (companyId: number | string) => RequestService.get(`${endpoints.users}/companyId/${companyId}`),
  getUserGroupsByCompanyId: (companyId: number | string) => RequestService.get(`${endpoints.userGroupsByCompanyId}/${companyId}`),
  createPassword: (data: { token: string; newPassword: string }) => RequestService.post('users/create-password', data),
  deleteUser: (id: number | string) => RequestService.patch(`${endpoints.delete}/${id}`),
  deleteUserGroup: (id: number | string) => RequestService.patch(`${endpoints.users}/delete/userGroup/${id}`),
  profilePhoto: (data: FormData) => RequestService.post(endpoints.profilePhoto, data),
  //Groups EPs
  createUserGroup: (data: any) => RequestService.post(`${endpoints.users}/createUserGroup`, data),
  updateUserGroup: (data: any) => RequestService.patch(`${endpoints.users}/updateUserGroup`, data),
  getUserGroups: () => RequestService.get(`${endpoints.users}/getUserGroups`),
  getUserGroupsByBranchId: (branchId: number | string) => RequestService.get(`${endpoints.users}/getUserGroupsByBranchId/${branchId}`),
  // Positions EPs
  getUserPositions: () => RequestService.get(`${endpoints.users}/getUserPositions`),
  createUserPositions: (data: ICreatePositionRequest) => RequestService.post(`${endpoints.users}/createUserPositions`, data),
  deleteUserPosition: (id: number | string) => RequestService.get(`${endpoints.users}/deleteUserPosition/${id}`),
  // Login History EPs
  getLoginHistory: (id: number | string) => RequestService.get(`${endpoints.users}/getLoginHistory/${id}`),
  getUsersByCompanyIdAndRoleId: (data: IUsersByCompanyIdAndRoleIdRequest) => RequestService.post(`${endpoints.users}/byCompanyIdRoleId`, data),
  findByCompanyIdAndPositionId: (data: { company_id: number | string; position_id: number | string }) => RequestService.post(`${endpoints.users}/findByCompanyIdAndPositionId`, data),
  resendPasswordEmail: (data: IResendPasswordEmailRequest) => RequestService.post(endpoints.resendPasswordEmail, data),
  // Leaving Codes EP
  getLeavingCodes: () => RequestService.get(`${endpoints.users}/leaving-codes`),
  // User Export EPs
  exportUsersToExcel: async (userIds: IExportUsersToExcelRequest) => {
    const response = await fetch(`${Api.getBaseUrl()}users/exportUsersToExcel`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
      body: JSON.stringify(userIds)
    });
    if (!response.ok) {
      toast.error("Dosya indirme işlemi başarısız oldu.")
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()

    if (!blob.type.includes("spreadsheet") && !blob.type.includes("excel")) {
      console.warn("Response might not be an Excel file:", blob.type)
    }

    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'users.xlsx'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(downloadUrl)

    toast.success("Dosya başarıyla indirildi.")
  },
  exportSingleUserToExcel: async (userId: IExportSingleUserToExcelRequest) => {
    const response = await fetch(`${Api.getBaseUrl()}users/exportSingleUserToExcel`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
      body: JSON.stringify(userId)
    });
    if (!response.ok) {
      toast.error("Dosya indirme işlemi başarısız oldu.")
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()

    if (!blob.type.includes("spreadsheet") && !blob.type.includes("excel")) {
      console.warn("Response might not be an Excel file:", blob.type)
    }

    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'user.xlsx'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(downloadUrl)

    toast.success("Dosya başarıyla indirildi.")
  },
  exportUsersToPdf: async (userIds: IExportUsersToPdfRequest) => {
    const response = await fetch(`${Api.getBaseUrl()}users/exportUsersToPdf`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
      body: JSON.stringify(userIds)
    });
    if (!response.ok) {
      toast.error("Dosya indirme işlemi başarısız oldu.")
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const blob = await response.blob()

    if (!blob.type.includes("spreadsheet") && !blob.type.includes("pdf")) {
      console.warn("Response might not be a PDF file:", blob.type)
    }

    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'users.pdf'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(downloadUrl)

    toast.success("Dosya başarıyla indirildi.")
  },
  exportSingleUserToPdf: async (userId: IExportSingleUserToPdfRequest) => {
    const response = await fetch(`${Api.getBaseUrl()}users/exportSingleUserToPdf`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
      body: JSON.stringify(userId)
    });
    if (!response.ok) {
      toast.error("Dosya indirme işlemi başarısız oldu.")
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const blob = await response.blob()

    if (!blob.type.includes("spreadsheet") && !blob.type.includes("pdf")) {
      console.warn("Response might not be a PDF file:", blob.type)
    }

    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'user.pdf'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(downloadUrl)

    toast.success("Dosya başarıyla indirildi.")
  },
  downloadUserImportTemplate: async () => {
    const response = await fetch(`${Api.getBaseUrl()}users/downloadUserImportTemplate`, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
    });
    if (!response.ok) {
      toast.error("Dosya indirme işlemi başarısız oldu.")
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()

    if (!blob.type.includes("spreadsheet") && !blob.type.includes("excel")) {
      console.warn("Response might not be an Excel file:", blob.type)
    }

    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'user-import-template.xlsx'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(downloadUrl)

    toast.success("Dosya başarıyla indirildi.")
  },
  importUsersFromExcel: (file: FormData) => RequestService.post(endpoints.importUsersFromExcel, file),
  getRolesHistory: () => RequestService.get(endpoints.getRolesHistory),
};

export default UsersService;
